/**
 * The pane's icons: Lucide, the set the web client uses, so the two products draw the
 * same pictures for the same things.
 *
 * Every icon goes through here rather than being imported where it is used, for two
 * reasons:
 *
 * - **Deep paths, never the barrel.** `import { X } from "lucide-react"` makes webpack
 *   parse all ~1,700 icons in development, which does not tree-shake -- the same trap
 *   as `@mui/icons-material` (see Gotchas in CLAUDE.md). One file per icon, one import.
 * - **One way to size and colour them: `className`.** `size-4`, `text-muted-foreground`.
 *   With no size class an icon is 20px -- set as the SVG's width and height attributes,
 *   not as a class, so that any class beats it and a ui/ Button (which sizes the icons
 *   that carry no `size-` class) can size the ones inside it.
 *
 * Stroke width is the web client's usual 1.85 rather than Lucide's 2, which reads as
 * heavy beside Hanken Grotesk at pane sizes.
 */
import * as React from "react";
import type { LucideIcon, LucideProps } from "lucide-react";
import { cn } from "../lib/utils";

import ArrowLeftGlyph from "lucide-react/dist/esm/icons/arrow-left.mjs";
import ArrowUpGlyph from "lucide-react/dist/esm/icons/arrow-up.mjs";
import BoldGlyph from "lucide-react/dist/esm/icons/bold.mjs";
import CheckGlyph from "lucide-react/dist/esm/icons/check.mjs";
import ChevronDownGlyph from "lucide-react/dist/esm/icons/chevron-down.mjs";
import ChevronRightGlyph from "lucide-react/dist/esm/icons/chevron-right.mjs";
import CircleAlertGlyph from "lucide-react/dist/esm/icons/circle-alert.mjs";
import CircleCheckGlyph from "lucide-react/dist/esm/icons/circle-check.mjs";
import ClockGlyph from "lucide-react/dist/esm/icons/clock.mjs";
import CloudCheckGlyph from "lucide-react/dist/esm/icons/cloud-check.mjs";
import CloudUploadGlyph from "lucide-react/dist/esm/icons/cloud-upload.mjs";
import CopyGlyph from "lucide-react/dist/esm/icons/copy.mjs";
import EllipsisGlyph from "lucide-react/dist/esm/icons/ellipsis.mjs";
import FileTextGlyph from "lucide-react/dist/esm/icons/file-text.mjs";
import FolderCheckGlyph from "lucide-react/dist/esm/icons/folder-check.mjs";
import FolderInputGlyph from "lucide-react/dist/esm/icons/folder-input.mjs";
import InfoGlyph from "lucide-react/dist/esm/icons/info.mjs";
import ItalicGlyph from "lucide-react/dist/esm/icons/italic.mjs";
import ListGlyph from "lucide-react/dist/esm/icons/list.mjs";
import ListOrderedGlyph from "lucide-react/dist/esm/icons/list-ordered.mjs";
import LockGlyph from "lucide-react/dist/esm/icons/lock.mjs";
import LockOpenGlyph from "lucide-react/dist/esm/icons/lock-open.mjs";
import LogOutGlyph from "lucide-react/dist/esm/icons/log-out.mjs";
import MailGlyph from "lucide-react/dist/esm/icons/mail.mjs";
import MoonGlyph from "lucide-react/dist/esm/icons/moon.mjs";
import NavigationGlyph from "lucide-react/dist/esm/icons/navigation.mjs";
import PenLineGlyph from "lucide-react/dist/esm/icons/pen-line.mjs";
import PencilGlyph from "lucide-react/dist/esm/icons/pencil.mjs";
import PlusGlyph from "lucide-react/dist/esm/icons/plus.mjs";
import RefreshCwGlyph from "lucide-react/dist/esm/icons/refresh-cw.mjs";
import SaveGlyph from "lucide-react/dist/esm/icons/save.mjs";
import SearchGlyph from "lucide-react/dist/esm/icons/search.mjs";
import SparklesGlyph from "lucide-react/dist/esm/icons/sparkles.mjs";
import SquareGlyph from "lucide-react/dist/esm/icons/square.mjs";
import SquarePenGlyph from "lucide-react/dist/esm/icons/square-pen.mjs";
import SunGlyph from "lucide-react/dist/esm/icons/sun.mjs";
import SunMoonGlyph from "lucide-react/dist/esm/icons/sun-moon.mjs";
import Trash2Glyph from "lucide-react/dist/esm/icons/trash-2.mjs";
import TriangleAlertGlyph from "lucide-react/dist/esm/icons/triangle-alert.mjs";
import UnderlineGlyph from "lucide-react/dist/esm/icons/underline.mjs";
import XGlyph from "lucide-react/dist/esm/icons/x.mjs";

const STROKE_WIDTH = 1.85;

export type IconProps = LucideProps;

const DEFAULT_SIZE = 20;

function icon(glyph: LucideIcon, name: string, { solid = false } = {}) {
  const Glyph = glyph;
  const Icon = React.forwardRef<SVGSVGElement, IconProps>(({ className, ...props }, ref) => (
    <Glyph
      ref={ref}
      aria-hidden
      size={DEFAULT_SIZE}
      strokeWidth={STROKE_WIDTH}
      className={cn("shrink-0", solid && "fill-current", className)}
      {...props}
    />
  ));
  Icon.displayName = `${name}Icon`;
  return Icon;
}

export const ArrowLeftIcon = icon(ArrowLeftGlyph, "ArrowLeft");
export const ArrowUpIcon = icon(ArrowUpGlyph, "ArrowUp");
export const BoldIcon = icon(BoldGlyph, "Bold");
export const CheckIcon = icon(CheckGlyph, "Check");
export const ChevronDownIcon = icon(ChevronDownGlyph, "ChevronDown");
export const ChevronRightIcon = icon(ChevronRightGlyph, "ChevronRight");
export const CircleAlertIcon = icon(CircleAlertGlyph, "CircleAlert");
export const CircleCheckIcon = icon(CircleCheckGlyph, "CircleCheck");
export const ClockIcon = icon(ClockGlyph, "Clock");
export const CloudCheckIcon = icon(CloudCheckGlyph, "CloudCheck");
export const CloudUploadIcon = icon(CloudUploadGlyph, "CloudUpload");
export const CopyIcon = icon(CopyGlyph, "Copy");
export const EllipsisIcon = icon(EllipsisGlyph, "Ellipsis");
export const FileTextIcon = icon(FileTextGlyph, "FileText");
export const FolderCheckIcon = icon(FolderCheckGlyph, "FolderCheck");
export const FolderInputIcon = icon(FolderInputGlyph, "FolderInput");
export const InfoIcon = icon(InfoGlyph, "Info");
export const ItalicIcon = icon(ItalicGlyph, "Italic");
export const ListIcon = icon(ListGlyph, "List");
export const ListOrderedIcon = icon(ListOrderedGlyph, "ListOrdered");
export const LockIcon = icon(LockGlyph, "Lock");
export const LockOpenIcon = icon(LockOpenGlyph, "LockOpen");
export const LogOutIcon = icon(LogOutGlyph, "LogOut");
export const MailIcon = icon(MailGlyph, "Mail");
export const MoonIcon = icon(MoonGlyph, "Moon");
export const NavigationIcon = icon(NavigationGlyph, "Navigation");
export const PenLineIcon = icon(PenLineGlyph, "PenLine");
export const PencilIcon = icon(PencilGlyph, "Pencil");
export const PlusIcon = icon(PlusGlyph, "Plus");
export const RefreshCwIcon = icon(RefreshCwGlyph, "RefreshCw");
export const SaveIcon = icon(SaveGlyph, "Save");
export const SearchIcon = icon(SearchGlyph, "Search");
export const SparklesIcon = icon(SparklesGlyph, "Sparkles");
/** Solid: it is the Stop button's glyph, and an outlined square reads as "checkbox". */
export const StopSquareIcon = icon(SquareGlyph, "StopSquare", { solid: true });
export const SquarePenIcon = icon(SquarePenGlyph, "SquarePen");
export const SunIcon = icon(SunGlyph, "Sun");
export const SunMoonIcon = icon(SunMoonGlyph, "SunMoon");
export const TrashIcon = icon(Trash2Glyph, "Trash");
export const TriangleAlertIcon = icon(TriangleAlertGlyph, "TriangleAlert");
export const UnderlineIcon = icon(UnderlineGlyph, "Underline");
export const XIcon = icon(XGlyph, "X");
